import React, { useState, useCallback } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AddTaskIcon from "@mui/icons-material/AddTask";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import ApiService from "../../../../services/ApiService";
import Logger from "../../../../services/Logger";
import WordsNotInUseFrenchNotice from "../../../pzl_common/WordsNotInUseFrenchNotice";

/**
 * Panel showing "words not in use" list (chips with character count and circle)
 * and a + icon to open the "Place Word in Puzzle" dialog. Used inside GridFillDialog when puzzle type is HIDDEN_MESSAGE.
 * Magic icon fetches Bedrock AI word suggestions and displays them in the panel.
 */
export default function GridFillNotInUsePanel({
    isFrenchPuzzle = false,
    wordsNotInUse = [],
    selectedWord = null,
    onSelectWord,
    onPlaceWord,
    onAddAllWords,
    filterByLength = null,
    gridId = null,
    emptyCellCount = 0,
    onShowSnackbar
}) {
    const [bedrockWords, setBedrockWords] = useState([]);
    const [loadingBedrock, setLoadingBedrock] = useState(false);

    const filtered = filterByLength != null ? wordsNotInUse.filter((w) => w.length === filterByLength) : wordsNotInUse;
    const sortedWords = [...filtered].sort((a, b) => a.length - b.length);

    const handleBedrockSearch = useCallback(async () => {
        if (!gridId || emptyCellCount <= 0) {
            onShowSnackbar?.("Cannot search: invalid puzzle or no empty cells", "error");
            return;
        }
        if (emptyCellCount >= 15) {
            onShowSnackbar?.("Bedrock AI supports words up to 14 letters. Empty cell count exceeds dictionary limit.", "warning");
            return;
        }
        setLoadingBedrock(true);
        setBedrockWords([]);
        try {
            const result = await ApiService.getData(`/v1/puzzle/grid-tpl/${gridId}/${emptyCellCount}/find-word/bedrock`);
            if (result?.words && Array.isArray(result.words)) {
                setBedrockWords(result.words);
                onShowSnackbar?.(`Found ${result.words.length} word(s) from ${result.source || "Bedrock"}`, "success");
            } else {
                setBedrockWords([]);
                onShowSnackbar?.("No words found for this length", "info");
            }
        } catch (err) {
            Logger.error("Error fetching Bedrock words:", err);
            onShowSnackbar?.("Error fetching words from Bedrock. Please try again.", "error");
            setBedrockWords([]);
        } finally {
            setLoadingBedrock(false);
        }
    }, [gridId, emptyCellCount, onShowSnackbar]);

    return (
        <Box sx={{ mb: 2, p: 1.5, borderRadius: 1, bgcolor: "action.hover", border: "1px solid", borderColor: "divider" }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 0.5, mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    Words not in use
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {!isFrenchPuzzle && onAddAllWords && wordsNotInUse.length > 0 && emptyCellCount > 0 && (
                        <Tooltip title="Try to add words not in use to fill empty cells">
                            <IconButton
                                size="small"
                                onClick={() => onAddAllWords()}
                                aria-label="Add all words"
                                sx={{ minWidth: "auto", "&:hover": { backgroundColor: "action.hover" } }}
                            >
                                <AddTaskIcon fontSize="small" color="primary" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {selectedWord && (() => {
                        const s = String(selectedWord).toLowerCase().trim();
                        const inNotInUse = !isFrenchPuzzle && sortedWords.some((w) => String(w).toLowerCase().trim() === s);
                        const inBedrock = bedrockWords.some((w) => String(w).toLowerCase().trim() === s);
                        return (inNotInUse || inBedrock);
                    })() && (
                        <Tooltip title="Place the selected word in puzzle as its hidden message">
                            <IconButton
                                size="small"
                                onClick={() => onPlaceWord && onPlaceWord(selectedWord)}
                                aria-label="Place word in puzzle"
                                sx={{ minWidth: "auto", "&:hover": { backgroundColor: "action.hover" } }}
                            >
                                <AddCircleOutlineIcon fontSize="small" color="primary" />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Find words using Bedrock AI">
                        <IconButton
                            size="small"
                            onClick={handleBedrockSearch}
                            disabled={loadingBedrock || emptyCellCount <= 0}
                            sx={{ minWidth: "auto", "&:hover": { backgroundColor: "action.hover" } }}
                            aria-label="Find words using Bedrock AI"
                        >
                            {loadingBedrock ? (
                                <CircularProgress size={24} />
                            ) : (
                                <AutoAwesomeIcon fontSize="small" color="primary" />
                            )}
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>
            {isFrenchPuzzle ? (
                <WordsNotInUseFrenchNotice />
            ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 100, overflow: "auto" }}>
                {sortedWords.length > 0 ? (
                    sortedWords.map((word, index) => (
                        <Chip
                            key={index}
                            label={
                                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                    <span>{word}</span>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            minWidth: 18,
                                            height: 18,
                                            borderRadius: "50%",
                                            bgcolor: "action.selected",
                                            border: "1px solid",
                                            borderColor: "divider",
                                            fontSize: "0.7rem",
                                            fontWeight: 600
                                        }}
                                    >
                                        {word.length}
                                    </Box>
                                </Box>
                            }
                            onClick={() => onSelectWord && onSelectWord(word)}
                            color={selectedWord === word ? "secondary" : "default"}
                            variant={selectedWord === word ? "filled" : "outlined"}
                            size="small"
                            sx={{ cursor: "pointer" }}
                        />
                    ))
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        No words not in use
                    </Typography>
                )}
            </Box>
            )}
            {bedrockWords.length > 0 && (
                <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontWeight: 600 }}>
                        AI suggestions ({bedrockWords.length})
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 80, overflow: "auto" }}>
                        {[...bedrockWords].sort((a, b) => a.length - b.length).map((word, index) => (
                            <Chip
                                key={index}
                                label={
                                    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                                        <span>{word}</span>
                                        <Box
                                            component="span"
                                            sx={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                minWidth: 18,
                                                height: 18,
                                                borderRadius: "50%",
                                                bgcolor: "action.selected",
                                                border: "1px solid",
                                                borderColor: "divider",
                                                fontSize: "0.7rem",
                                                fontWeight: 600
                                            }}
                                        >
                                            {word.length}
                                        </Box>
                                    </Box>
                                }
                                onClick={() => onSelectWord && onSelectWord(word)}
                                color={selectedWord === word ? "secondary" : "default"}
                                variant={selectedWord === word ? "filled" : "outlined"}
                                size="small"
                                sx={{ cursor: "pointer" }}
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </Box>
    );
}
